import { createSelector } from 'reselect';
import {
  FeaturesForPlans,
  RequiredPlan,
  SubscriptionFeatureFlag,
  UpgradePlan,
} from '../controllers/FeatureFlags';
import { PageBillingType } from '../interfaces/connected-page';
import CompanyModel, { CompanyLevelFeatureFlag } from '../model/CompanyModel';
import { StoreState } from '../store-interfaces';
import { activeFacebookPageSelector } from './facebookPageSelector';
import { activeCompanySelector } from './viewSelectors';

export type FeatureFlag = SubscriptionFeatureFlag | CompanyLevelFeatureFlag;

const convertStringArrayToHash = <
  T extends SubscriptionFeatureFlag | CompanyLevelFeatureFlag,
>(
  features: T[],
) =>
  features.reduce((flags, flag) => ({ ...flags, ...{ [flag]: true } }), {}) as {
    [K in T]: boolean | undefined;
  };

export const selectCompanyFeatureFlags = (activeCompany?: CompanyModel) => {
  const companyFeatures = activeCompany?.additionalFeatures || [];
  return convertStringArrayToHash<CompanyLevelFeatureFlag>(companyFeatures);
};

export const companyFeatureFlagsSelector = createSelector(
  [activeCompanySelector],
  selectCompanyFeatureFlags,
);

const subscriptionFeatureFlags = (state: StoreState) =>
  state.featureFlags.subscriptionFeatureFlags;

export const subscriptionFeatureFlagsSelector = createSelector(
  [subscriptionFeatureFlags],
  features => convertStringArrayToHash<SubscriptionFeatureFlag>(features),
);

// Notes:
// 1. We might want to consider which the rules for combining the features.
// For now I'm just using the simple approach of merging.
// 2. I envision we might find this approach helpful and later add (if needed):
// - user-level feature flags
// A/B test-level feature flags

export const combinedFeatureFlagsSelector = createSelector(
  [companyFeatureFlagsSelector, subscriptionFeatureFlagsSelector],
  (companyFeatures, subscriptionFeatures) => ({
    ...companyFeatures,
    ...subscriptionFeatures,
  }),
);

export const showInstaChampAlertSelector = createSelector(
  [activeFacebookPageSelector, companyFeatureFlagsSelector],
  (facebookPage, featureFlags) => {
    const instagramUsername =
      facebookPage?.availableInstagramAccountsData?.username;
    return [
      Boolean(instagramUsername && featureFlags.instachamp),
      instagramUsername,
    ] as const;
  },
);
export const getNextBillingTypeForFeature = (
  features: ReturnType<typeof combinedFeatureFlagsSelector>,
  featuresForPlans: FeaturesForPlans,
  feature?: SubscriptionFeatureFlag | CompanyLevelFeatureFlag,
) => {
  const { november2021Pricing } = features;

  if (!feature || features[feature]) {
    return undefined;
  }

  const featurePlans = featuresForPlans[feature];

  switch (featurePlans?.[0]) {
    case 'pro':
      return november2021Pricing
        ? PageBillingType.ProUnicorn
        : PageBillingType.Pro;
    case 'unicorn':
      return PageBillingType.ProUnicorn;
    case 'team2020':
      return PageBillingType.TeamLabel;
    default:
      return november2021Pricing ? PageBillingType.ProUnicorn : undefined;
  }
};

export const getPlanFromBillingType = (
  billingType?: PageBillingType,
): RequiredPlan => {
  switch (billingType) {
    case PageBillingType.Pro:
      return 'pro';
    case PageBillingType.ProUnicorn:
      return 'unicorn';
    case PageBillingType.TeamLabel:
      return 'team';
    default:
      return undefined;
  }
};

const companyFeatureToPlanMapping = {
  instagram: [] as UpgradePlan[],
  twilio: ['pro', 'unicorn', 'team2020'] as UpgradePlan[],
};

const getCompanyLevelFeaturesForPlans = (state: StoreState) => {
  const companyFeatures = companyFeatureFlagsSelector(state);
  const availableCompanyFeaturesMapping: Partial<
    Record<CompanyLevelFeatureFlag, UpgradePlan[]>
  > = {};

  Object.keys(companyFeatures).forEach((k: CompanyLevelFeatureFlag) => {
    availableCompanyFeaturesMapping[k] = companyFeatureToPlanMapping[k];
  });

  return availableCompanyFeaturesMapping;
};

export const nextBillingTypeForFeatureSelector = (
  state: StoreState,
  feature?: SubscriptionFeatureFlag | CompanyLevelFeatureFlag,
) => {
  const features = combinedFeatureFlagsSelector(state);
  const featuresForPlans = {
    ...state.featureFlags.featuresForPlans,
    ...getCompanyLevelFeaturesForPlans,
  };

  return getNextBillingTypeForFeature(features, featuresForPlans, feature);
};
